import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Updates.css';

const unitLogo = '/images/LP_Unit_logo.png';

// Version history - add new entries at the top
const updates = [
  {
    version: 'v1.0.6.2',
    date: 'March 9, 2026',
    changes: [
      'My Request History (BETA)',
      'Added "My Requests" page for all users to view their submitted requests',
      'Shows request type, status, and date (no time-in-queue visible to users)',
      'View Details shows full configuration and selections',
      'Requests sorted by most recent first',
      'Armorer Filter Fix',
      'Fixed "Filter by Armorer" to properly match assigned armorers',
      'Now handles multi-armorer assignments correctly',
      'Supports both ID-based and name-based legacy assignments',
      'Armorer Dropdown Fix',
      'Fixed armorer assignment dropdown to show users with "displayRole" field',
      'Dropdown now checks both "role" and "displayRole" fields for armorer/admin users',
      'New armorers (e.g., A. Garcia) now appear in the Assigned To dropdown'
    ]
  },
  {
    version: 'v1.0.6.1',
    date: 'February 28, 2026',
    changes: [
      'Armorer Assignment Fix',
      'Fixed armorer dropdown to show users with "displayRole" field (A. Garcia now appears)',
      'Dropdown now checks both "role" and "displayRole" fields for armorer/admin users',
      'Request Status Updates',
      'Added "Closed Out" status - immediately grays out and closes the request',
      '"Pushed to ModPack" no longer grays out after 6 hours',
      'Added "Closed Out" to Filter by Status dropdown'
    ]
  },
  {
    version: 'v1.0.6',
    date: 'February 5, 2026',
    changes: [
      'Discord OAuth Integration',
      'Replaced email/password login with Discord OAuth',
      'User roles now sync automatically from Discord server roles',
      'Username displays normalized format (e.g., "J. Draco" instead of full rank)',
      'Admin Panel Improvements',
      'All Requests tab now sorted by newest first',
      'Donation Requests tab now sorted by newest first',
      'Added green notification badge for pending donations',
      'Request Queue Updates',
      'Added "Payment Received" status option (purple)',
      'Fixed dropdown menus getting cut off at bottom of table',
      'Armorer assignment dropdown now opens upward',
      'Request Details modal now shows revision fields (Helmet/Vest/Belt details)',
      'Request Details modal now shows ZAP change fields',
      'Request Details modal now shows custom item details',
      'Bug Fixes',
      'Fixed ZAP Change submission error',
      'Fixed Vest/Helmet revision submission errors',
      'Fixed all commission forms to work with Discord OAuth',
      'Removed S&S Precision M320 Holster from belt options'
    ]
  },
  {
    version: 'v1.0.5',
    date: 'January 29, 2026',
    changes: [
      'Navbar & Homepage Overhaul',
      'Added Custom Headwear commission option ($20 per item)',
      'Homepage cards now display in 2x2 grid layout',
      'Notification Badges',
      'Armorers see unassigned request count on "Behind the Cage"',
      'Admins see pending user approval count on "Admin"',
      'Modify Existing Pages',
      'Vest & Belt revision now has separate fields for each',
      'Helmet revision has dedicated Helmet field',
      'Admin Panel Improvements',
      'Added Reject button for pending users with reason logging',
      'User Management now has sorting (by date, username, role)',
      'Rejected users are logged to audit trail and deleted',
      'Request Queue Updates',
      'Time in Queue coloring: 0-3 days (none), 4-6 days (yellow), 6+ days (red)',
      'Pushed to ModPack for 6+ hours now grays out instead of red',
      'Multi-armorer assignment via checkbox dropdown',
      'Pricing Update',
      'All Custom ACEX options now $10 (was $5)'
    ]
  },
  {
    version: 'v1.0.4',
    date: 'January 27, 2026',
    changes: [
      'Navbar Consolidation',
      'Combined Vest & Belts and Helmet dropdowns into single "Build Requests" dropdown',
      'Build Requests now includes: Vest & Belts, Helmet, T-Shirt/Hoodie/Patch with nested submenus',
      'Moved ZAP Change button to center of main page (above cards)',
      'Moved Contact button to floating position in bottom-right corner',
      'Removed main heading text from homepage',
      'Donation Perk Tracking System',
      'Added JSOC Perks, Tier 1 Perks, Tier 2 Perks, Tier 3 Perks columns to Donation Requests table',
      'Perks cascade based on tier purchased (JSOC gets all 4, Tier 1 gets 3, etc.)',
      'Unpurchased tier levels automatically display N/A',
      'Yes/No dropdown for admins to mark perks as redeemed',
      'Timestamp automatically recorded when perk is marked as "Yes"',
      'Added Status dropdown to Donations (Pending, Partially Complete, Completed, Denied)',
      'Donations table now horizontally scrollable for all columns',
      'Added audit logging for admin delete actions',
      'Improved table spacing and styling for Donation Requests'
    ]
  },
  {
    version: 'v1.0.3',
    date: 'January 26, 2026',
    changes: [
      'Donation Page Overhaul',
      'Donation tier cards now display in single column (stacked vertically)',
      'Donation page styling updated to dark theme matching vest/helmet pages',
      'Added quarterly enrollment header at top of Donate page',
      'Updated JSOC tier: MILSIM item with international shipping notice',
      'Updated Tier 3: Custom Locker covers quarterly changes',
      'Donation disclaimer now numbered (1-6) and left-justified',
      'Added enrollment period clause to donation disclaimer',
      'Updated donation form processing message with E. Malick contact',
      'Added Chest Rig section to Vest Commission with 7 options',
      'Updated Custom ACEX price from $7 to $5 across all pages',
      'Admin pages now use CSQD-Vol-II.jpg background',
      'Metrics page now uses C_SQD_Vol_V.jpg background',
      'Behind the Cage page uses updated background',
      'Armorers can now assign requests (restored functionality)',
      'Fixed popup styling to match across all commission pages'
    ]
  },
  {
    version: 'v1.0.2',
    date: 'January 26, 2026',
    changes: [
      'Major UI/UX Overhaul',
      'Added Donate button (green) in navbar with full donation page',
      'Donation tiers: JSOC ($100), Tier 1 ($75), Tier 2 ($50), Tier 3 ($25)',
      'Donation disclaimer popup with acknowledgment workflow',
      'Added Admin dropdown menu: User Management & Donation Requests',
      'Zap Change now opens popup instead of separate page',
      'Moved Updates button to right of Helmet dropdown',
      'Added T-Shirt, Hoodie, or Patch commission ($25 per item) with multi-item cart',
      'Added "Other/Please Specify" option to Base Vest and Base Helmet',
      'Added Premium Asset (+$10) and Custom ACEX (+$5) options to all commissions',
      'Added Cart section showing price breakdown and totals',
      'Created Modify Existing Vest/Helmet pages with free-form description',
      'New request types: New - Helmet, New - Vest, New - Custom, Revision - Helmet, Revision - Vest, ZAP',
      'Added filter dropdowns to View Requests: Filter by Type, Filter by Armorer',
      'Only Admins can access Metrics page'
    ]
  },
  {
    version: 'v1.0.1',
    date: 'January 25, 2026',
    changes: [
      'Fixed commission page dropdown menus now expand inline (no longer clipped)',
      'Updated background images for all pages',
      'Main landing page: LP_Background_Photo.png',
      'Vest & Helmet commission pages: new_build_background_photo.png',
      'All other pages (Login, Admin, Metrics, Updates, etc.): coming_soon_bg.png',
      'Fixed Firestore rules to allow admin delete of requests',
      'Added all helmet fields to Firestore allowed fields',
      'Fixed various image path and case-sensitivity issues'
    ]
  },
  {
    version: 'v0.8',
    date: 'January 25, 2026',
    changes: [
      'Added Metrics dashboard for armorers',
      'Added timer tracking for requests (time in queue, time in status)',
      'Admin panel now shows user/armorer/admin counts',
      'Admins can now delete commission requests',
      'Behind the Cage now only shows armorers in assignment dropdown',
      'Added View Latest Updates page',
      'Reorganized Behind the Cage into dropdown with Requests & Metrics'
    ]
  },
  {
    version: 'v0.7',
    date: 'January 24, 2026',
    changes: [
      'Added complete Vest & Belts commission form',
      'Added complete Helmet commission form with GSGM NOD lock',
      'Changed Vest to Vest & Belts',
      'Added all vest, placard, belt, and holster options',
      'Added all helmet configuration options',
      'Nested dropdown menus for complex selections'
    ]
  },
  {
    version: 'v0.6',
    date: 'January 24, 2026',
    changes: [
      'Implemented email/password authentication',
      'Removed Google OAuth login',
      'Added user signup with admin approval workflow',
      'Added 48-hour session expiry for regular users',
      'Created Admin panel for user management'
    ]
  },
  {
    version: 'v0.5',
    date: 'January 24, 2026',
    changes: [
      'Added commission info popup (ZAP, Discord, PayPal)',
      'Added Build_photo.png as preview background',
      'Updated styling to match dark theme',
      'Fixed background image loading issues'
    ]
  },
  {
    version: 'v0.4',
    date: 'January 24, 2026',
    changes: [
      'Rebranded to Creature Supply',
      'Added Zap Change button to navbar',
      'Made logo/title clickable to return home',
      'Added modify existing disclaimer popup'
    ]
  },
  {
    version: 'v0.3',
    date: 'January 24, 2026',
    changes: [
      'Created Behind the Cage armorer dashboard',
      'Added request tracking system',
      'Implemented status workflow (Unassigned → In Progress → Ready → Complete)',
      'Added armorer assignment functionality'
    ]
  },
  {
    version: 'v0.2',
    date: 'January 24, 2026',
    changes: [
      'Set up Firebase authentication',
      'Created role-based access (User, Armorer, Admin)',
      'Implemented protected routes',
      'Added Firestore database integration'
    ]
  },
  {
    version: 'v0.1',
    date: 'January 24, 2026',
    changes: [
      'Initial landing page design',
      'Created Vest and Helmet cards',
      'Basic navigation structure',
      'Dark military tactical theme'
    ]
  }
];

function Updates() {
  const { user, logout } = useAuth();

  return (
    <div className="updates-page" style={{ backgroundImage: `url(/images/C-SQD-Vol-VIII.jpg)`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }}>
      <nav className="updates-navbar">
        <Link to="/" className="nav-left">
          <img src={unitLogo} alt="Logo" className="unit-logo" />
          <h1 className="site-title">Creature Supply</h1>
        </Link>
        <div className="nav-right">
          <Link to="/" className="nav-link">Home</Link>
          {user && <button className="logout-btn" onClick={logout}>Logout</button>}
        </div>
      </nav>

      <main className="updates-content">
        <div className="updates-header">
          <h2>Latest Updates</h2>
          <p className="current-version">Current Version: {updates[0].version}</p>
        </div>

        <div className="updates-timeline">
          {updates.map((update, index) => (
            <div key={update.version} className={`update-entry ${index === 0 ? 'latest' : ''}`}>
              <div className="update-marker">
                <div className="marker-dot"></div>
                {index < updates.length - 1 && <div className="marker-line"></div>}
              </div>
              <div className="update-content">
                <div className="update-header">
                  <span className="update-version">{update.version}</span>
                  <span className="update-date">{update.date}</span>
                </div>
                <ul className="update-changes">
                  {update.changes.map((change, i) => (
                    <li key={i}>{change}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export default Updates;
